/**
 * GuestCheckout.js - Payment-First Guest Booking Flow
 * 
 * Flow:
 * 1. Customer selects service and enters info
 * 2. Customer sees price breakdown
 * 3. Customer pays via Stripe
 * 4. Booking created ONLY after payment succeeds
 * 5. Owner notified immediately
 */
import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  CreditCard, Calendar, Clock, MapPin, User, Phone, Mail,
  ChevronRight, ChevronLeft, AlertCircle, CheckCircle, Loader2,
  Car, Home, Package, Shirt, ShoppingCart, DollarSign
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import api from '../services/api';

// Service type icons
const SERVICE_ICONS = {
  rides: Car,
  cleaning: Home,
  laundry: Shirt,
  courier: Package,
  errands: ShoppingCart,
};

const GuestCheckout = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const preSelectedServiceId = searchParams.get('service');
  const isRide = searchParams.get('type') === 'ride';

  // Steps
  const [step, setStep] = useState(1); // 1: Service, 2: Info, 3: Review, 4: Payment
  const [loading, setLoading] = useState(false);
  const [services, setServices] = useState([]);
  
  // Form data
  const [selectedService, setSelectedService] = useState(null);
  const [formData, setFormData] = useState({
    // Guest info
    name: '',
    email: '',
    phone: '',
    // Schedule
    scheduled_date: '',
    scheduled_time: '',
    duration_hours: 2,
    // Service address
    address: '',
    city: '',
    state: 'NC',
    zip_code: '',
    // Ride specific
    pickup_address: '',
    pickup_city: '',
    pickup_zip: '',
    dropoff_address: '',
    dropoff_city: '',
    dropoff_zip: '',
    passenger_count: 1,
    vehicle_type: 'sedan',
    // Special
    special_instructions: '',
  });
  
  // Quote
  const [quote, setQuote] = useState(null);
  const [quoteLoading, setQuoteLoading] = useState(false);

  // Fetch services
  useEffect(() => {
    const fetchServices = async () => {
      try {
        const res = await api.get('/services');
        setServices(res.data || []);
        
        if (preSelectedServiceId) {
          const service = res.data.find(s => s.id === preSelectedServiceId);
          if (service) {
            setSelectedService(service);
            setStep(2);
          }
        }
      } catch (error) {
        toast.error('Failed to load services');
      }
    };
    fetchServices();
  }, [preSelectedServiceId]);

  // Get price quote
  const getQuote = async () => {
    if (!selectedService) return;
    
    setQuoteLoading(true);
    try {
      const isRideService = selectedService.service_type === 'rides';
      
      const quoteData = {
        service_id: selectedService.id,
        scheduled_date: formData.scheduled_date,
        scheduled_time: formData.scheduled_time,
        duration_hours: formData.duration_hours,
        guest: {
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
        },
        address: {
          address: formData.address,
          city: formData.city,
          state: formData.state,
          zip_code: formData.zip_code,
        },
        special_instructions: formData.special_instructions,
        is_ride: isRideService,
      };
      
      if (isRideService) {
        quoteData.ride_locations = {
          pickup_address: formData.pickup_address,
          pickup_city: formData.pickup_city,
          pickup_zip: formData.pickup_zip,
          dropoff_address: formData.dropoff_address,
          dropoff_city: formData.dropoff_city,
          dropoff_zip: formData.dropoff_zip,
        };
        quoteData.passenger_count = formData.passenger_count;
        quoteData.vehicle_type = formData.vehicle_type;
      }
      
      const res = await api.post('/checkout/quote', quoteData);
      setQuote(res.data);
    } catch (error) {
      toast.error('Failed to get price quote');
    } finally {
      setQuoteLoading(false);
    }
  };

  // Create payment session and redirect to Stripe
  const proceedToPayment = async () => {
    if (!selectedService || !quote) return;
    
    setLoading(true);
    try {
      const isRideService = selectedService.service_type === 'rides';
      
      const checkoutData = {
        service_id: selectedService.id,
        scheduled_date: formData.scheduled_date,
        scheduled_time: formData.scheduled_time,
        duration_hours: formData.duration_hours,
        guest: {
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
        },
        address: {
          address: formData.address || formData.pickup_address,
          city: formData.city || formData.pickup_city,
          state: formData.state,
          zip_code: formData.zip_code || formData.pickup_zip,
        },
        special_instructions: formData.special_instructions,
        is_ride: isRideService,
      };
      
      if (isRideService) {
        checkoutData.ride_locations = {
          pickup_address: formData.pickup_address,
          pickup_city: formData.pickup_city,
          pickup_zip: formData.pickup_zip,
          dropoff_address: formData.dropoff_address,
          dropoff_city: formData.dropoff_city,
          dropoff_zip: formData.dropoff_zip,
        };
        checkoutData.passenger_count = formData.passenger_count;
        checkoutData.vehicle_type = formData.vehicle_type;
      }
      
      const res = await api.post('/checkout/session', checkoutData);
      
      // Redirect to Stripe checkout - handle iframe safely
      if (res.data.checkout_url) {
        try {
          if (window.top !== window.self) {
            // We're in an iframe - redirect the top window
            window.top.location.href = res.data.checkout_url;
          } else {
            window.location.href = res.data.checkout_url;
          }
        } catch (e) {
          // Cross-origin iframe - open in new tab
          window.open(res.data.checkout_url, '_blank');
        }
      } else {
        toast.error('Failed to create payment session');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Payment system error');
    } finally {
      setLoading(false);
    }
  };

  // Validate current step
  const canProceed = () => {
    switch (step) {
      case 1:
        return selectedService !== null;
      case 2:
        const isRideService = selectedService?.service_type === 'rides';
        const hasGuestInfo = formData.name && formData.email && formData.phone;
        const hasSchedule = formData.scheduled_date && formData.scheduled_time;
        
        if (isRideService) {
          return hasGuestInfo && hasSchedule && 
            formData.pickup_address && formData.pickup_city && formData.pickup_zip &&
            formData.dropoff_address && formData.dropoff_city && formData.dropoff_zip;
        }
        return hasGuestInfo && hasSchedule && formData.address && formData.city && formData.zip_code;
      case 3:
        return quote !== null;
      default:
        return false;
    }
  };

  // Next step
  const nextStep = async () => {
    if (step === 2) {
      await getQuote();
    }
    setStep(prev => Math.min(prev + 1, 4));
  };

  // Step 1: Select Service
  const renderServiceSelection = () => {
    const serviceTypes = ['rides', 'cleaning', 'laundry', 'courier', 'errands'];
    
    return (
      <div className="space-y-6">
        <div className="text-center mb-8">
          <h2 className="text-2xl font-bold mb-2">Select a Service</h2>
          <p className="text-[var(--text-muted)]">Choose the service you need</p>
        </div>
        
        {serviceTypes.map(type => {
          const typeServices = services.filter(s => s.service_type === type);
          if (typeServices.length === 0) return null;
          
          const Icon = SERVICE_ICONS[type] || Package;
          
          return (
            <div key={type} className="space-y-3">
              <h3 className="font-semibold capitalize flex items-center gap-2">
                <Icon className="w-5 h-5" />
                {type === 'errands' ? 'Errands & Tasks' : type}
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {typeServices.map(service => (
                  <button
                    key={service.id}
                    onClick={() => setSelectedService(service)}
                    className={`p-4 rounded-lg border text-left transition-all ${
                      selectedService?.id === service.id
                        ? 'border-[var(--primary)] bg-[var(--primary)]/5 ring-2 ring-[var(--primary)]'
                        : 'border-[var(--border-primary)] hover:border-[var(--primary)]'
                    }`}
                  >
                    <p className="font-medium">{service.name}</p>
                    <p className="text-sm text-[var(--text-muted)]">{service.description}</p>
                    <p className="text-[var(--primary)] font-semibold mt-2">
                      {service.service_type === 'rides' 
                        ? 'Distance-based pricing'
                        : `$${service.hourly_rate || service.base_rate}/hr`
                      }
                    </p>
                  </button>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  // Step 2: Enter Information
  const renderInfoForm = () => {
    const isRideService = selectedService?.service_type === 'rides';
    
    return (
      <div className="space-y-6">
        <div className="text-center mb-8">
          <h2 className="text-2xl font-bold mb-2">Your Information</h2>
          <p className="text-[var(--text-muted)]">
            {selectedService?.name} • No account required
          </p>
        </div>
        
        {/* Guest Info */}
        <div className="card p-4 space-y-4">
          <h3 className="font-semibold flex items-center gap-2">
            <User className="w-4 h-4" /> Contact Information
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">Full Name *</label>
              <Input
                value={formData.name}
                onChange={e => setFormData(prev => ({ ...prev, name: e.target.value }))}
                placeholder="John Doe"
                data-testid="guest-name"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Phone *</label>
              <Input
                value={formData.phone}
                onChange={e => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                placeholder="(919) 555-1234"
                data-testid="guest-phone"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Email *</label>
            <Input
              type="email"
              value={formData.email}
              onChange={e => setFormData(prev => ({ ...prev, email: e.target.value }))}
              placeholder="john@example.com"
              data-testid="guest-email"
            />
          </div>
        </div>
        
        {/* Schedule */}
        <div className="card p-4 space-y-4">
          <h3 className="font-semibold flex items-center gap-2">
            <Calendar className="w-4 h-4" /> Schedule
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">Date *</label>
              <Input
                type="date"
                value={formData.scheduled_date}
                onChange={e => setFormData(prev => ({ ...prev, scheduled_date: e.target.value }))}
                min={new Date().toISOString().split('T')[0]}
                data-testid="schedule-date"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Time *</label>
              <Input
                type="time"
                value={formData.scheduled_time}
                onChange={e => setFormData(prev => ({ ...prev, scheduled_time: e.target.value }))}
                data-testid="schedule-time"
              />
            </div>
          </div>
          {!isRideService && (
            <div>
              <label className="block text-sm font-medium mb-1">Duration (hours)</label>
              <select
                value={formData.duration_hours}
                onChange={e => setFormData(prev => ({ ...prev, duration_hours: parseFloat(e.target.value) }))}
                className="w-full px-3 py-2 rounded-lg border border-[var(--border-primary)]"
              >
                {[1, 2, 3, 4, 5, 6, 7, 8].map(h => (
                  <option key={h} value={h}>{h} hour{h > 1 ? 's' : ''}</option>
                ))}
              </select>
            </div>
          )}
        </div>
        
        {/* Location */}
        <div className="card p-4 space-y-4">
          <h3 className="font-semibold flex items-center gap-2">
            <MapPin className="w-4 h-4" /> 
            {isRideService ? 'Trip Details' : 'Service Location'}
          </h3>
          
          {isRideService ? (
            <>
              {/* Pickup */}
              <div className="space-y-3">
                <p className="text-sm font-medium text-green-600">Pickup Location</p>
                <Input
                  value={formData.pickup_address}
                  onChange={e => setFormData(prev => ({ ...prev, pickup_address: e.target.value }))}
                  placeholder="123 Main St"
                  data-testid="pickup-address"
                />
                <div className="grid grid-cols-2 gap-3">
                  <Input
                    value={formData.pickup_city}
                    onChange={e => setFormData(prev => ({ ...prev, pickup_city: e.target.value }))}
                    placeholder="City"
                    data-testid="pickup-city"
                  />
                  <Input
                    value={formData.pickup_zip}
                    onChange={e => setFormData(prev => ({ ...prev, pickup_zip: e.target.value }))}
                    placeholder="ZIP Code"
                    data-testid="pickup-zip"
                  />
                </div>
              </div>
              {/* Dropoff */}
              <div className="space-y-3">
                <p className="text-sm font-medium text-red-600">Dropoff Location</p>
                <Input
                  value={formData.dropoff_address}
                  onChange={e => setFormData(prev => ({ ...prev, dropoff_address: e.target.value }))}
                  placeholder="456 Oak Ave"
                  data-testid="dropoff-address"
                />
                <div className="grid grid-cols-2 gap-3">
                  <Input
                    value={formData.dropoff_city}
                    onChange={e => setFormData(prev => ({ ...prev, dropoff_city: e.target.value }))}
                    placeholder="City"
                    data-testid="dropoff-city"
                  />
                  <Input
                    value={formData.dropoff_zip}
                    onChange={e => setFormData(prev => ({ ...prev, dropoff_zip: e.target.value }))}
                    placeholder="ZIP Code"
                    data-testid="dropoff-zip"
                  />
                </div>
              </div>
              {/* Vehicle */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Vehicle Type</label>
                  <select
                    value={formData.vehicle_type}
                    onChange={e => setFormData(prev => ({ ...prev, vehicle_type: e.target.value }))}
                    className="w-full px-3 py-2 rounded-lg border border-[var(--border-primary)]"
                  >
                    <option value="sedan">Sedan</option>
                    <option value="suv">SUV (+30%)</option>
                    <option value="van">Van (+50%)</option>
                    <option value="wheelchair">Wheelchair Accessible (+40%)</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Passengers</label>
                  <select
                    value={formData.passenger_count}
                    onChange={e => setFormData(prev => ({ ...prev, passenger_count: parseInt(e.target.value) }))}
                    className="w-full px-3 py-2 rounded-lg border border-[var(--border-primary)]"
                  >
                    {[1, 2, 3, 4, 5, 6].map(n => (
                      <option key={n} value={n}>{n}</option>
                    ))}
                  </select>
                </div>
              </div>
            </>
          ) : (
            <>
              <Input
                value={formData.address}
                onChange={e => setFormData(prev => ({ ...prev, address: e.target.value }))}
                placeholder="Street Address"
                data-testid="address"
              />
              <div className="grid grid-cols-2 gap-3">
                <Input
                  value={formData.city}
                  onChange={e => setFormData(prev => ({ ...prev, city: e.target.value }))}
                  placeholder="City"
                  data-testid="city"
                />
                <Input
                  value={formData.zip_code}
                  onChange={e => setFormData(prev => ({ ...prev, zip_code: e.target.value }))}
                  placeholder="ZIP Code"
                  data-testid="zip-code"
                />
              </div>
            </>
          )}
        </div>
        
        {/* Special Instructions */}
        <div className="card p-4">
          <label className="block text-sm font-medium mb-1">Special Instructions (Optional)</label>
          <textarea
            value={formData.special_instructions}
            onChange={e => setFormData(prev => ({ ...prev, special_instructions: e.target.value }))}
            className="w-full px-3 py-2 rounded-lg border border-[var(--border-primary)] resize-none"
            rows={3}
            placeholder="Any special requests or notes..."
          />
        </div>
      </div>
    );
  };

  // Step 3: Review & Price
  const renderReview = () => {
    const isRideService = selectedService?.service_type === 'rides';
    
    return (
      <div className="space-y-6">
        <div className="text-center mb-8">
          <h2 className="text-2xl font-bold mb-2">Review & Pay</h2>
          <p className="text-[var(--text-muted)]">
            Confirm your booking details before payment
          </p>
        </div>
        
        {quoteLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-[var(--primary)]" />
          </div>
        ) : quote ? (
          <>
            {/* Booking Summary */}
            <div className="card p-6 space-y-4">
              <h3 className="font-bold text-lg border-b pb-2">Booking Summary</h3>
              
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-lg bg-[var(--primary)]/10 flex items-center justify-center">
                  {(() => {
                    const Icon = SERVICE_ICONS[selectedService?.service_type] || Package;
                    return <Icon className="w-6 h-6 text-[var(--primary)]" />;
                  })()}
                </div>
                <div>
                  <p className="font-semibold">{selectedService?.name}</p>
                  <p className="text-sm text-[var(--text-muted)]">
                    {formData.scheduled_date} at {formData.scheduled_time}
                  </p>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-[var(--text-muted)]">Customer</p>
                  <p className="font-medium">{formData.name}</p>
                </div>
                <div>
                  <p className="text-[var(--text-muted)]">Phone</p>
                  <p className="font-medium">{formData.phone}</p>
                </div>
                <div className="col-span-2">
                  <p className="text-[var(--text-muted)]">Email</p>
                  <p className="font-medium">{formData.email}</p>
                </div>
              </div>
              
              {isRideService ? (
                <div className="border-t pt-4 space-y-2">
                  <div className="flex items-start gap-2">
                    <div className="w-2 h-2 rounded-full bg-green-500 mt-2"></div>
                    <div>
                      <p className="text-sm text-[var(--text-muted)]">Pickup</p>
                      <p className="font-medium">{formData.pickup_address}, {formData.pickup_city}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-2">
                    <div className="w-2 h-2 rounded-full bg-red-500 mt-2"></div>
                    <div>
                      <p className="text-sm text-[var(--text-muted)]">Dropoff</p>
                      <p className="font-medium">{formData.dropoff_address}, {formData.dropoff_city}</p>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="border-t pt-4">
                  <p className="text-sm text-[var(--text-muted)]">Service Location</p>
                  <p className="font-medium">{formData.address}, {formData.city}, {formData.state} {formData.zip_code}</p>
                </div>
              )}
            </div>
            
            {/* Price Breakdown */}
            <div className="card p-6 space-y-3 bg-gradient-to-br from-[var(--primary)]/5 to-transparent">
              <h3 className="font-bold text-lg border-b pb-2 flex items-center gap-2">
                <DollarSign className="w-5 h-5" />
                Price Breakdown
              </h3>
              
              {quote.breakdown && (
                <div className="space-y-2 text-sm">
                  {quote.breakdown.base_fare !== undefined && (
                    <div className="flex justify-between">
                      <span>Base Fare</span>
                      <span>${quote.breakdown.base_fare?.toFixed(2)}</span>
                    </div>
                  )}
                  {quote.breakdown.distance_charge !== undefined && (
                    <div className="flex justify-between">
                      <span>Distance ({quote.breakdown.distance_miles?.toFixed(1)} mi)</span>
                      <span>${quote.breakdown.distance_charge?.toFixed(2)}</span>
                    </div>
                  )}
                  {/* Only show hourly breakdown for non-ride services */}
                  {!isRideService && quote.breakdown.base_amount !== undefined && (
                    <div className="flex justify-between">
                      <span>Service ({formData.duration_hours}h × ${quote.breakdown.hourly_rate}/hr)</span>
                      <span>${quote.breakdown.base_amount?.toFixed(2)}</span>
                    </div>
                  )}
                  {quote.breakdown.mileage_fee > 0 && (
                    <div className="flex justify-between">
                      <span>Travel Fee ({quote.breakdown.distance_miles?.toFixed(1)} mi)</span>
                      <span>${quote.breakdown.mileage_fee?.toFixed(2)}</span>
                    </div>
                  )}
                  {quote.breakdown.booking_fee > 0 && (
                    <div className="flex justify-between">
                      <span>Booking Fee</span>
                      <span>${quote.breakdown.booking_fee?.toFixed(2)}</span>
                    </div>
                  )}
                </div>
              )}
              
              <div className="border-t pt-3 mt-3">
                <div className="flex justify-between items-center">
                  <span className="text-xl font-bold">Total</span>
                  <span className="text-2xl font-bold text-[var(--primary)]">
                    ${quote.total_amount?.toFixed(2)}
                  </span>
                </div>
              </div>
            </div>
            
            {/* Payment Notice */}
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium text-amber-800">Payment Required</p>
                <p className="text-sm text-amber-700">
                  Payment will be processed securely via Stripe. Your booking will be confirmed 
                  immediately after successful payment.
                </p>
              </div>
            </div>
          </>
        ) : (
          <div className="text-center py-8 text-[var(--text-muted)]">
            Failed to get price quote. Please go back and try again.
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-[var(--background)]">
      {/* Header */}
      <div className="bg-[var(--primary)] text-white py-4 px-4">
        <div className="max-w-2xl mx-auto">
          <h1 className="text-xl font-bold">NC Help Hub</h1>
          <p className="text-sm opacity-90">Book a Service - No Account Required</p>
        </div>
      </div>
      
      {/* Progress Steps */}
      <div className="bg-white border-b py-4 px-4">
        <div className="max-w-2xl mx-auto">
          <div className="flex items-center justify-between">
            {['Service', 'Details', 'Review', 'Payment'].map((label, idx) => (
              <div key={label} className="flex items-center">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                  step > idx + 1 ? 'bg-green-500 text-white' :
                  step === idx + 1 ? 'bg-[var(--primary)] text-white' :
                  'bg-gray-200 text-gray-500'
                }`}>
                  {step > idx + 1 ? <CheckCircle className="w-5 h-5" /> : idx + 1}
                </div>
                <span className={`ml-2 text-sm hidden sm:inline ${
                  step === idx + 1 ? 'font-medium' : 'text-[var(--text-muted)]'
                }`}>
                  {label}
                </span>
                {idx < 3 && (
                  <ChevronRight className="w-4 h-4 mx-2 text-gray-300" />
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
      
      {/* Content */}
      <div className="max-w-2xl mx-auto p-4 pb-24">
        <motion.div
          key={step}
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -20 }}
          transition={{ duration: 0.2 }}
        >
          {step === 1 && renderServiceSelection()}
          {step === 2 && renderInfoForm()}
          {step === 3 && renderReview()}
        </motion.div>
      </div>
      
      {/* Bottom Actions */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t p-4">
        <div className="max-w-2xl mx-auto flex items-center justify-between gap-4">
          {step > 1 ? (
            <Button 
              variant="outline" 
              onClick={() => setStep(prev => prev - 1)}
              disabled={loading}
            >
              <ChevronLeft className="w-4 h-4 mr-1" />
              Back
            </Button>
          ) : (
            <Button 
              variant="outline" 
              onClick={() => navigate('/')}
            >
              Cancel
            </Button>
          )}
          
          {step < 3 ? (
            <Button 
              onClick={nextStep}
              disabled={!canProceed()}
              className="btn-primary flex-1 max-w-[200px]"
            >
              Continue
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          ) : (
            <Button 
              onClick={proceedToPayment}
              disabled={loading || !quote}
              className="btn-primary flex-1 max-w-[250px]"
              data-testid="pay-now-btn"
            >
              {loading ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <CreditCard className="w-4 h-4 mr-2" />
              )}
              Pay ${quote?.total_amount?.toFixed(2) || '0.00'} Now
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default GuestCheckout;
